import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';
import Lottie from 'lottie-react';

import { ModifyValue } from 'containers/ProductDetail/hooks/useProductDetaillHooks';
import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import Column from 'components/common/Column';
import Row from 'components/common/Row';

import { ICON_CLOSE_OUTLINE, ICON_PLUS_MONO } from 'assets/icons';
import { useImageUpdateFormHooks } from './hooks/useImageUpdateFormHooks';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import SaveButton from '../SaveButton';
import { Product, ProductState } from 'types/api';
import HackleManager from 'utils/hackle';
import lottie from 'assets/lotties/image_upload_lottie.json';
import ModalHeader from './ModalHeader';
import TitleContainer from 'containers/ProductRegist/components/TitleContainer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImageUploadForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  border-radius: 16px;
  border: 1px solid #f4f4f5;
  background: #f7f7f8;

  color: var(--gray-gray-06, #989ba2);

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: -0.6px;
  cursor: pointer;

  input {
    position: absolute;
    display: none;
    border: 1px solid red;
    width: 100%;
    height: 100%;
  }
`;

const ImageNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: #feeee5;
  color: #f55a00;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.6px;

  ul {
    margin: 0;
    padding-left: 21px;
    list-style: disc;
  }

  li {
    margin: 0;
  }
`;
const DeleteContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  padding: 6px;
  border-radius: 50%;
  background: rgba(46, 47, 51, 0.88);
`;

const RepresentativeBadge = styled.div`
  position: absolute;
  display: flex;
  left: 8px;
  top: 8px;
  padding: 6px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 99px;
  border: 1px solid rgba(55, 56, 60, 0.16);
  background: #fff;
  color: #1b1c1e;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const ImageContainer = styled.div<{ $width: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  width: ${props => props.$width}px;
  height: ${props => props.$width}px;

  border-radius: 16px;
  background-color: #f4f4f5;
`;

interface ImageModifyContainerIProps {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  productInfo: Product;
  modifyValue: ModifyValue;
  setModifyValue: Dispatch<SetStateAction<ModifyValue>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  imageUpdateFormHooks: ReturnType<typeof useImageUpdateFormHooks>;
}

const ImageModifyContainer: React.FC<ImageModifyContainerIProps> = props => {
  const {
    isMobile,
    productParamInput,
    productInfo,
    modifyValue,
    setModifyValue,
    handleOnChangeProductParamInput,
    imageUpdateFormHooks,
  } = props;

  const { width, ref } = useResizeDetector();

  const {
    fileInputRef,
    imageThumbnail,
    copyHeaderContents,
    isAbled,
    isLoading,
    handleFileChange,
    handleImageUploadClick,
    handleDeleteImage,
    handleResetImageThumbnail,
  } = imageUpdateFormHooks;

  const handleRegistHeaderContents = () => {
    handleOnChangeProductParamInput('headerContents', copyHeaderContents);
  };

  const imageWidth = isMobile ? (width - 52) / 2 : (width - 92) / 3;
  const MAX_FILES = 5;

  useEffect(() => {
    HackleManager.logProductModifyImageView(
      Number(productParamInput.id),
      productParamInput.title,
    );
  }, []);

  const renderImageUploadForm = (): JSX.Element => {
    return (
      <Column gap={isMobile ? '16px' : '32px'} align="flex-start">
        <ImageNotice>
          <ul>
            <li>
              첫번째 이미지는 대표 이미지로 사용되며, 텍스트, 로고를 표시할 수
              없어요.
            </li>
            <li>용량은 10MB 이하 확장자는 JPG, PNG만 가능해요.</li>
            <li>
              활동이 잘 드러나는 사진을 최소 1장 ~ 최대 5장까지 등록해주세요.
            </li>
          </ul>
        </ImageNotice>
        <Row
          gap="12px"
          justifyContent={isMobile ? 'space-between' : 'flex-start'}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${imageWidth}px, auto))`,
            width: '100%',
          }}
        >
          <ImageUploadForm
            onClick={() => {
              handleImageUploadClick();
            }}
            style={{
              width: `${imageWidth}px`,
              height: `${imageWidth}px`,
            }}
          >
            <img src={ICON_PLUS_MONO} alt="image upload icon" />
            {imageThumbnail.length}/5
            <input
              ref={fileInputRef}
              type="file"
              multiple={false}
              onChange={handleFileChange}
              accept=".jpg, .png, .jpeg"
            />
          </ImageUploadForm>
          {copyHeaderContents.map((item, idx) => {
            return (
              <Row style={{ position: 'relative' }} key={idx}>
                {idx === 0 && (
                  <RepresentativeBadge>대표사진</RepresentativeBadge>
                )}
                <img
                  src={item.uri}
                  alt="image thumbnail"
                  key={idx}
                  style={{
                    width: `${imageWidth}px`,
                    height: `${imageWidth}px`,
                    borderRadius: '16px',
                    border: '1px solid rgba(112, 115, 124, 0.05)',
                    objectFit: 'cover',
                  }}
                />
                <DeleteContainer
                  onClick={() => {
                    if (copyHeaderContents.length !== 1) {
                      handleDeleteImage(idx);
                    } else {
                      window.showToast(
                        '하나의 이미지는 삭제할 수 없습니다',
                        'failed',
                      );
                    }
                  }}
                >
                  <img src={ICON_CLOSE_OUTLINE} width={16} height={16} />
                </DeleteContainer>
              </Row>
            );
          })}
          {isLoading && (
            <ImageContainer $width={imageWidth}>
              <Lottie animationData={lottie} style={{ width: '50px' }} />
            </ImageContainer>
          )}
        </Row>
      </Column>
    );
  };

  const renderContents = (): JSX.Element => {
    if (isMobile) {
      return (
        <ModalContainer
          isMobile={isMobile}
          isAbled={productInfo.status !== ProductState.CLOSED && isAbled}
          onClose={() => {
            setModifyValue({ ...modifyValue, modifyStatus: '' });
          }}
          onCloseText=" "
          onAcceptText="적용"
          onClick={() => {
            HackleManager.logProductModifyItemSaveComplete(
              Number(productInfo.id),
              productInfo.title,
              '사진',
            );

            handleRegistHeaderContents();
            setModifyValue({
              ...modifyValue,
              isModify: true,
              modifyStatus: '',
            });
          }}
        >
          <Column style={{ position: 'relative', padding: '0 20px' }}>
            <ModalHeader
              title="사진 수정하기"
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
            />
            {renderImageUploadForm()}
          </Column>
        </ModalContainer>
      );
    }

    return (
      <React.Fragment>
        <Row
          align="center"
          style={{ width: '100%', padding: '0px 28px 0px 40px' }}
        >
          <TitleContainer title="사진 수정하기" isMobile={isMobile} />
        </Row>
        <Column gap="32px" style={{ padding: '0px 28px 34px 40px' }}>
          {renderImageUploadForm()}
          {productInfo.status !== ProductState.CLOSED && (
            <SaveButton
              isAbled={isAbled}
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
              handleResetItem={handleResetImageThumbnail}
              handleRegistItem={() => {
                HackleManager.logProductModifyItemSaveComplete(
                  Number(productInfo.id),
                  productInfo.title,
                  '사진',
                );

                handleRegistHeaderContents();
              }}
            />
          )}
        </Column>
      </React.Fragment>
    );
  };

  return <Container ref={ref}>{renderContents()}</Container>;
};

export default ImageModifyContainer;
